/**
 * Reading and writing a stored notification template.
 *
 * It lives apart from the wizard's editor so the request page does not have to import a
 * 1300-line component to render a line of prose, and so there is exactly one answer to what
 * counts as a variable — the one thing this and the API's `tplReplace` must agree on.
 */

/**
 * A template, split into the parts a person can see: prose they typed, variables we wrote, and —
 * for the body, which is several paragraphs rather than a line — the ends of their lines.
 */
export type TemplateSegment = { kind: "text"; text: string } | { kind: "variable"; name: string } | { kind: "break" };

/**
 * The same expression the API's `tplReplace` uses, so what this component treats as a variable and
 * what the mail path treats as a variable can never disagree. Anything that is not a well-formed
 * `{{…}}` — a stray brace, an empty pair — stays literal text and survives a round trip untouched.
 */
const VARIABLE_PATTERN = /\{\{([^}]+)\}\}/g;

/**
 * Lossless: `serialiseTemplate(parseTemplate(t)) === t` for every `t`, with or without `lines`.
 *
 * Every text segment is surrounded by text — a pill and a line ending never touch each other —
 * and the list begins and ends with one, possibly empty. That is what lets the editor put a caret
 * either side of every pill and at both ends of every line, including an empty one.
 *
 * `lines` splits the prose at each newline so the editor can lay the body out as lines. The
 * single-line fields leave it off and keep their text whole; nothing else changes.
 */
export function parseTemplate(template: string, lines = false): TemplateSegment[] {
	const segments: TemplateSegment[] = [];
	let cursor = 0;

	const pushText = (text: string): void => {
		if (!lines) {
			segments.push({ kind: "text", text });
			return;
		}

		for (const [index, line] of text.split("\n").entries()) {
			if (index > 0) segments.push({ kind: "break" });
			segments.push({ kind: "text", text: line });
		}
	};

	for (const match of template.matchAll(VARIABLE_PATTERN)) {
		const at = match.index;
		pushText(template.slice(cursor, at));
		segments.push({ kind: "variable", name: match[1] });
		cursor = at + match[0].length;
	}

	pushText(template.slice(cursor));
	return segments;
}

/**
 * Display labels for the keys the API resolves.
 *
 * Display only. The keys themselves are load-bearing — `getTplParams` builds the object these
 * names index into, and renaming one would silently unresolve every campaign already using it —
 * so this maps rather than replaces. A key with no entry here (a capture field, or a column from
 * an uploaded list) is humanised from its own name.
 */
export const VARIABLE_LABELS: Record<string, string> = {
	call__url: "Recording link",
	campaign__title: "Campaign name",
	contact__company: "Company",
	contact__name: "Contact name",
	contact__phone: "Phone number",
	contact__sales_manager: "Sales manager",
	contact__sales_rep: "Sales rep",
	last_call__dateTime: "Call date & time",
	last_call__duration: "Call duration",
	lead__disposition: "Outcome",
	lead__summary: "Call summary",
	system__date: "Today's date",
	system__time: "Time sent",
};

/** `lead__room_count` → "Room count". The fallback for anything the map does not name. */
export function humaniseVariable(name: string): string {
	const bare = name.trim().split("__").at(-1) ?? name.trim();
	const words = bare
		.replaceAll(/([a-z\d])([A-Z])/g, "$1 $2")
		.replaceAll(/[_-]+/g, " ")
		.trim()
		.toLowerCase();

	return words.slice(0, 1).toUpperCase() + words.slice(1);
}

/**
 * A stored template as one line of plain prose, for the places that have no room for pills — a
 * collapsed section summary, for instance. Same substitution, no markup.
 */
export function describeTemplate(template: string, extra: Record<string, string> = {}): string {
	return parseTemplate(template)
		.map((segment) => {
			if (segment.kind === "text") return segment.text;
			if (segment.kind === "break") return "\n";

			const name = segment.name.trim();
			return extra[name] ?? VARIABLE_LABELS[name] ?? humaniseVariable(name);
		})
		.join("")
		.trim();
}

/** Back to the string the API stores. The braces are ours, never the client's to type. */
export function serialiseTemplate(segments: TemplateSegment[]): string {
	return segments
		.map((segment) => {
			if (segment.kind === "text") return segment.text;
			return segment.kind === "break" ? "\n" : `{{${segment.name}}}`;
		})
		.join("");
}

/** The first `{{…}}` in a template — the variable the To line actually resolves against. */
export function firstVariable(template: string): string | null {
	for (const segment of parseTemplate(template)) {
		if (segment.kind === "variable") return segment.name.trim();
	}

	return null;
}
